/**
 * Running fix assembler (slice D). A running fix is made by advancing an
 * earlier line of position to a later time, then intersecting it with a later
 * line of position. It is how we get a fix from a single object observed twice,
 * by carrying the first bearing forward along the run.
 *
 * Advancing a line of position is its own named step, not something hidden
 * inside the running fix, so it is exposed separately (buildAdvancedLOP). The
 * advance method matters and is labelled, because transferring by water track
 * and by ground track give different answers, and the difference is the tide.
 *
 * The maths is the tested nav transferLine / runningFix; this layer assembles
 * the objects and wires the parents so the construction is preserved.
 */
import { transferLine, runningFix, type BearingLine, type Run } from '@/nav';
import type { LineOfPosition, PositionPoint, BearingObservation, TimeStamp } from './objects';

export interface RunningFixLeg {
  /** The mark the bearing was taken on. */
  markPosition: [number, number];
  markLabel: string;
  /** Bearing in degrees true. */
  bearing: number;
  time?: TimeStamp;
}

export interface BuildRunningFixParams {
  earlier: RunningFixLeg;
  /** The run between the two observations. */
  run: { cogDeg: number; sogKnots: number; hours: number };
  /** How the earlier LOP is carried forward. Water track is the default. */
  advanceMethod?: 'water_track' | 'ground_track';
  later: RunningFixLeg;
  newId: (prefix: string) => string;
}

export interface BuiltRunningFix {
  earlierObs: BearingObservation;
  earlierLOP: LineOfPosition;
  advancedLOP: LineOfPosition;
  laterObs: BearingObservation;
  laterLOP: LineOfPosition;
  runningFix: PositionPoint;
}

/** Advance a line of position forward by a run, producing an advanced LOP. */
export function buildAdvancedLOP(
  lop: LineOfPosition,
  run: Run,
  method: 'water_track' | 'ground_track',
  newId: (prefix: string) => string,
): LineOfPosition {
  const transferred = transferLine(lop.geometry, run);
  return {
    id: newId('alop'),
    kind: 'advanced_lop',
    geometry: transferred,
    advanceMethod: method,
    advancedFrom: lop.id,
    label: 'transferred',
    source: { method: 'manual' },
    parents: [lop.id],
    children: [],
    userPlaced: false,
  };
}

export function buildRunningFix(params: BuildRunningFixParams): BuiltRunningFix | null {
  const { earlier, run, later, newId } = params;
  const method = params.advanceMethod ?? 'water_track';

  const earlierLine: BearingLine = {
    type: 'bearing',
    from: earlier.markPosition,
    bearingDeg: earlier.bearing,
  };
  const laterLine: BearingLine = {
    type: 'bearing',
    from: later.markPosition,
    bearingDeg: later.bearing,
  };

  // Tested nav maths: transfer the earlier line by the run, intersect with the
  // later line. We use the same transfer for the advanced-LOP object below.
  const navRun: Run = { cogDeg: run.cogDeg, sogKnots: run.sogKnots, hours: run.hours };
  const { fix } = runningFix(earlierLine, navRun, laterLine);
  if (!fix) return null;

  const earlierObs = bearingObs(earlier, newId);
  const earlierLOP: LineOfPosition = {
    id: newId('lop'),
    kind: 'bearing_lop',
    geometry: earlierLine,
    observation: earlierObs.id,
    time: earlier.time,
    label: '1',
    source: { method: 'manual' },
    parents: [earlierObs.id],
    children: [],
    userPlaced: true,
  };

  const advancedLOP = buildAdvancedLOP(earlierLOP, navRun, method, newId);

  const laterObs = bearingObs(later, newId);
  const laterLOP: LineOfPosition = {
    id: newId('lop'),
    kind: 'bearing_lop',
    geometry: laterLine,
    observation: laterObs.id,
    time: later.time,
    label: '2',
    source: { method: 'manual' },
    parents: [laterObs.id],
    children: [],
    userPlaced: true,
  };

  const runningFixPoint: PositionPoint = {
    id: newId('rfix'),
    kind: 'position_point',
    pointType: 'running_fix',
    position: fix,
    time: later.time,
    label: later.time ? `R Fix ${hhmm(later.time)}` : 'R Fix',
    source: { method: 'manual' },
    // The running fix is the intersection of the advanced earlier LOP and the
    // later LOP, so those are its parents.
    parents: [advancedLOP.id, laterLOP.id],
    children: [],
    userPlaced: false,
  };

  return { earlierObs, earlierLOP, advancedLOP, laterObs, laterLOP, runningFix: runningFixPoint };
}

function bearingObs(leg: RunningFixLeg, newId: (p: string) => string): BearingObservation {
  return {
    id: newId('obs'),
    kind: 'bearing_obs',
    object: null,
    bearing: leg.bearing,
    bearingType: 'true',
    time: leg.time,
    source: { method: 'manual' },
    parents: [],
    children: [],
    userPlaced: true,
  };
}

function hhmm(time: TimeStamp): string {
  const d = new Date(time.iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getUTCHours()).padStart(2, '0')}${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
